import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
    BookOpenIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline';

const LessonPlanManager = () => {
    const { get, post, put, del } = useApi();

    const [lessonPlans, setLessonPlans] = useState([]);
    const [schemes, setSchemes] = useState([]);
    const [subStrands, setSubStrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedScheme, setSelectedScheme] = useState(null);
    const [formData, setFormData] = useState({
        sub_strand: '',
        title: '',
        lesson_date: '',
        duration_minutes: 40,
        learning_outcomes: '',
        introduction: '',
        development: '',
        conclusion: '',
        inquiry_questions: '',
        learning_activities: '',
        resources_needed: '',
        assessment_strategies: '',
        creation_mode: 'manual'
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [filterPublished, setFilterPublished] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [plansData, schemesData, subStrandsData] = await Promise.all([
                get('/api/cbc/lesson-plans/'),
                get('/api/cbc/schemes-of-work/'),
                get('/api/cbc/sub-strands/')
            ]);

            setLessonPlans(plansData);
            setSchemes(schemesData);
            setSubStrands(subStrandsData);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load lesson plans. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreate = () => {
        setSelectedPlan(null);
        setFormData({
            sub_strand: '',
            title: '',
            lesson_date: '',
            duration_minutes: 40,
            learning_outcomes: '',
            introduction: '',
            development: '',
            conclusion: '',
            inquiry_questions: '',
            learning_activities: '',
            resources_needed: '',
            assessment_strategies: '',
            creation_mode: 'manual'
        });
        setShowModal(true);
    };

    const handleEdit = (plan) => {
        setSelectedPlan(plan);
        setFormData({
            sub_strand: plan.sub_strand,
            title: plan.title,
            lesson_date: plan.lesson_date,
            duration_minutes: plan.duration_minutes,
            learning_outcomes: plan.learning_outcomes,
            introduction: plan.introduction,
            development: plan.development,
            conclusion: plan.conclusion,
            inquiry_questions: plan.inquiry_questions,
            learning_activities: plan.learning_activities,
            resources_needed: plan.resources_needed,
            assessment_strategies: plan.assessment_strategies,
            creation_mode: plan.creation_mode
        });
        setShowModal(true);
    };

    const handleView = async (plan) => {
        try {
            const detailData = await get(`/api/cbc/lesson-plans/${plan.id}/`);
            setSelectedPlan(detailData);
            setShowDetailModal(true);
        } catch (err) {
            console.error('Error fetching lesson plan details:', err);
            alert('Failed to load lesson plan details.');
        }
    };

    const handleDelete = async (planId) => {
        if (!window.confirm('Are you sure you want to delete this lesson plan? This action cannot be undone.')) {
            return;
        }

        try {
            await del(`/api/cbc/lesson-plans/${planId}/`);
            await fetchData();
        } catch (err) {
            console.error('Error deleting lesson plan:', err);
            alert('Failed to delete lesson plan. Please try again.');
        }
    };

    const handleTogglePublish = async (planId, currentStatus) => {
        try {
            const action = currentStatus ? 'unpublish' : 'publish';
            await post(`/api/cbc/lesson-plans/${planId}/${action}/`);
            await fetchData();
        } catch (err) {
            console.error('Error toggling publish status:', err);
            alert('Failed to update publish status. Please try again.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSubmitting(true);

            if (selectedPlan) {
                await put(`/api/cbc/lesson-plans/${selectedPlan.id}/`, formData);
            } else {
                await post('/api/cbc/lesson-plans/', formData);
            }

            await fetchData();
            setShowModal(false);
        } catch (err) {
            console.error('Error saving lesson plan:', err);
            alert(err.response?.data?.error || 'Failed to save lesson plan. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleGenerateFromScheme = async () => {
        if (!selectedScheme) return;

        try {
            setSubmitting(true);
            await post(`/api/cbc/schemes-of-work/${selectedScheme}/generate_lesson_plans/`);
            await fetchData();
            setShowGenerateModal(false);
            setSelectedScheme(null);
            alert('Lesson plans generated successfully!');
        } catch (err) {
            console.error('Error generating lesson plans:', err);
            alert(err.response?.data?.error || 'Failed to generate lesson plans. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredPlans = lessonPlans.filter(plan => {
        if (filterPublished === '') return true;
        return filterPublished === 'published' ? plan.is_published : !plan.is_published;
    });

    if (loading) {
        return <LoadingSpinner fullScreen />;
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-black text-[#18216D] uppercase tracking-tight">Lesson Plans</h1>
                        <p className="text-sm text-slate-500 mt-2 font-bold">Create and manage your daily lesson plans</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowGenerateModal(true)}
                            className="px-6 py-3 bg-[#FFC425] text-[#18216D] rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#FFD666] transition-all shadow-lg flex items-center gap-2"
                        >
                            <SparklesIcon className="w-4 h-4" />
                            Generate from SoW
                        </button>
                        <button
                            onClick={handleCreate}
                            className="px-6 py-3 bg-[#18216D] text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-lg shadow-indigo-900/10 flex items-center gap-2"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Create Manual
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center gap-4">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                        Status:
                    </label>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFilterPublished('')}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${filterPublished === ''
                                ? 'bg-[#18216D] text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilterPublished('published')}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${filterPublished === 'published'
                                ? 'bg-emerald-500 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                        >
                            Published
                        </button>
                        <button
                            onClick={() => setFilterPublished('draft')}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${filterPublished === 'draft'
                                ? 'bg-amber-500 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                        >
                            Drafts
                        </button>
                    </div>
                </div>
            </div>

            {/* Lesson Plan List */}
            {error ? (
                <div className="p-6 bg-rose-50 border border-rose-200 rounded-2xl">
                    <p className="text-rose-600 font-bold">{error}</p>
                </div>
            ) : filteredPlans.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredPlans.map(plan => (
                        <div key={plan.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-lg transition-all">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${plan.is_published
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                            : 'bg-amber-50 text-amber-700 border-amber-100'
                                            }`}>
                                            {plan.is_published ? 'Published' : 'Draft'}
                                        </span>
                                        <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${plan.creation_mode === 'automated'
                                            ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                            : 'bg-slate-50 text-slate-700 border-slate-100'
                                            }`}>
                                            {plan.creation_mode === 'automated' ? 'Auto' : 'Manual'}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-black text-[#18216D] tracking-tight">{plan.title}</h3>
                                    <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mt-1">
                                        {plan.sub_strand_name}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Date</p>
                                    <p className="text-sm font-bold text-[#18216D]">
                                        {new Date(plan.lesson_date).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Duration</p>
                                    <p className="text-sm font-bold text-[#18216D]">{plan.duration_minutes} mins</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <button
                                    onClick={() => handleTogglePublish(plan.id, plan.is_published)}
                                    className={`px-4 py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${plan.is_published
                                        ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                                        : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                                        }`}
                                >
                                    {plan.is_published ? 'Unpublish' : 'Publish'}
                                </button>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleView(plan)}
                                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                        title="View Details"
                                    >
                                        <EyeIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleEdit(plan)}
                                        className="p-2 text-slate-400 hover:text-[#18216D] hover:bg-slate-50 rounded-lg transition-all"
                                        title="Edit"
                                    >
                                        <PencilIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(plan.id)}
                                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                        title="Delete"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
                    <BookOpenIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No lesson plans found</p>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200 overflow-y-auto">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl my-8">
                        <form onSubmit={handleSubmit}>
                            <div className="p-8 border-b border-slate-100">
                                <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight">
                                    {selectedPlan ? 'Edit' : 'Create'} Lesson Plan
                                </h2>
                            </div>

                            <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Sub-Strand *
                                        </label>
                                        <select
                                            required
                                            value={formData.sub_strand}
                                            onChange={(e) => setFormData(prev => ({ ...prev, sub_strand: e.target.value }))}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-bold text-[#18216D]"
                                        >
                                            <option value="">Select Sub-Strand</option>
                                            {subStrands.map(ss => (
                                                <option key={ss.id} value={ss.id}>{ss.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Lesson Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.lesson_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, lesson_date: e.target.value }))}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-bold text-[#18216D]"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Title *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.title}
                                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-bold text-[#18216D]"
                                            placeholder="e.g., Introduction to Fractions"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Duration (minutes) *
                                        </label>
                                        <input
                                            type="number"
                                            required
                                            min="1"
                                            value={formData.duration_minutes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-bold text-[#18216D]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                        Learning Outcomes
                                    </label>
                                    <textarea
                                        value={formData.learning_outcomes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, learning_outcomes: e.target.value }))}
                                        rows={3}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                        placeholder="What will students learn?"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                        Introduction
                                    </label>
                                    <textarea
                                        value={formData.introduction}
                                        onChange={(e) => setFormData(prev => ({ ...prev, introduction: e.target.value }))}
                                        rows={3}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                        placeholder="How will you introduce the lesson?"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                        Development
                                    </label>
                                    <textarea
                                        value={formData.development}
                                        onChange={(e) => setFormData(prev => ({ ...prev, development: e.target.value }))}
                                        rows={4}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                        placeholder="Main lesson activities and content..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                        Conclusion
                                    </label>
                                    <textarea
                                        value={formData.conclusion}
                                        onChange={(e) => setFormData(prev => ({ ...prev, conclusion: e.target.value }))}
                                        rows={2}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                        placeholder="How will you wrap up the lesson?"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Inquiry Questions
                                        </label>
                                        <textarea
                                            value={formData.inquiry_questions}
                                            onChange={(e) => setFormData(prev => ({ ...prev, inquiry_questions: e.target.value }))}
                                            rows={3}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                            placeholder="Key questions to ask students..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Learning Activities
                                        </label>
                                        <textarea
                                            value={formData.learning_activities}
                                            onChange={(e) => setFormData(prev => ({ ...prev, learning_activities: e.target.value }))}
                                            rows={3}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                            placeholder="Student activities and exercises..."
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Resources Needed
                                        </label>
                                        <textarea
                                            value={formData.resources_needed}
                                            onChange={(e) => setFormData(prev => ({ ...prev, resources_needed: e.target.value }))}
                                            rows={2}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                            placeholder="Materials, tools, etc..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">
                                            Assessment Strategies
                                        </label>
                                        <textarea
                                            value={formData.assessment_strategies}
                                            onChange={(e) => setFormData(prev => ({ ...prev, assessment_strategies: e.target.value }))}
                                            rows={2}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-medium text-[#18216D]"
                                            placeholder="How will you assess learning?"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="p-8 border-t border-slate-100 flex justify-end gap-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-6 py-3 bg-[#18216D] text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#0D164F] transition-all disabled:opacity-50 shadow-lg shadow-indigo-900/10"
                                >
                                    {submitting ? 'Saving...' : selectedPlan ? 'Update' : 'Create'} Plan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Generate from Scheme Modal */}
            {showGenerateModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md">
                        <div className="p-8 border-b border-slate-100">
                            <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight">Generate Lesson Plans</h2>
                            <p className="text-xs text-slate-500 mt-2">Automatically create lesson plans from a Scheme of Work</p>
                        </div>

                        <div className="p-8">
                            <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-3">
                                Select Scheme of Work
                            </label>
                            <select
                                value={selectedScheme || ''}
                                onChange={(e) => setSelectedScheme(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 focus:bg-white transition-all font-bold text-[#18216D]"
                            >
                                <option value="">-- Select a scheme --</option>
                                {schemes.map(scheme => (
                                    <option key={scheme.id} value={scheme.id}>
                                        {scheme.title} ({scheme.learning_area_name})
                                    </option>
                                ))}
                            </select>

                            {selectedScheme && (
                                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                                    <p className="text-xs text-indigo-700 font-bold">
                                        ✨ This will automatically generate lesson plans based on the weekly breakdown in the selected scheme.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="p-8 border-t border-slate-100 flex justify-end gap-4">
                            <button
                                onClick={() => {
                                    setShowGenerateModal(false);
                                    setSelectedScheme(null);
                                }}
                                className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerateFromScheme}
                                disabled={!selectedScheme || submitting}
                                className="px-6 py-3 bg-[#FFC425] text-[#18216D] rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#FFD666] transition-all disabled:opacity-50 shadow-lg flex items-center gap-2"
                            >
                                {submitting ? 'Generating...' : (
                                    <>
                                        <SparklesIcon className="w-4 h-4" />
                                        Generate Plans
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail View Modal */}
            {showDetailModal && selectedPlan && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200 overflow-y-auto">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl my-8">
                        <div className="p-8 border-b border-slate-100 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight">{selectedPlan.title}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${selectedPlan.is_published
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : 'bg-amber-50 text-amber-700'
                                        }`}>
                                        {selectedPlan.is_published ? 'Published' : 'Draft'}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="h-10 w-10 rounded-full bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-500 flex items-center justify-center transition-all"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                            </button>
                        </div>

                        <div className="p-8 max-h-[60vh] overflow-y-auto space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-50 rounded-xl">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Date</p>
                                    <p className="text-sm font-bold text-[#18216D]">
                                        {new Date(selectedPlan.lesson_date).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl">
                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Duration</p>
                                    <p className="text-sm font-bold text-[#18216D]">{selectedPlan.duration_minutes} minutes</p>
                                </div>
                            </div>

                            {selectedPlan.learning_outcomes && (
                                <div>
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Learning Outcomes</h3>
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.learning_outcomes}</p>
                                </div>
                            )}

                            {selectedPlan.introduction && (
                                <div>
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Introduction</h3>
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.introduction}</p>
                                </div>
                            )}

                            {selectedPlan.development && (
                                <div>
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Development</h3>
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.development}</p>
                                </div>
                            )}

                            {selectedPlan.conclusion && (
                                <div>
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Conclusion</h3>
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.conclusion}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {selectedPlan.inquiry_questions && (
                                    <div>
                                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Inquiry Questions</h3>
                                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.inquiry_questions}</p>
                                    </div>
                                )}

                                {selectedPlan.learning_activities && (
                                    <div>
                                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Learning Activities</h3>
                                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.learning_activities}</p>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {selectedPlan.resources_needed && (
                                    <div>
                                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Resources Needed</h3>
                                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.resources_needed}</p>
                                    </div>
                                )}

                                {selectedPlan.assessment_strategies && (
                                    <div>
                                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Assessment Strategies</h3>
                                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedPlan.assessment_strategies}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-8 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LessonPlanManager;
